import orHomePlansCarousel from './or-home-plans-carousel.variants';

// components
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { MlHeader } from '@/components/molecules/ml-header/ml-header';

// types
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';
import { IOrHomePlansCarouselProps } from './or-home-plans-carousel.types';
import { EThemes } from '@/lib/contentstack-sdk/types';

// hooks + utils
import { JSX, Suspense } from 'react';
import { getHomePlanUrl } from '@/components/helpers/formatters/home-plans';
import { MlCard } from '@/components/molecules/ml-card/ml-card';
import { ETagVariants } from '@/components/atoms/at-tag/at-tag.types';
import { EMlCardsTypes } from '@/components/molecules/ml-card/ml-card.types';
import { getFeatures, getImage } from './utils';
import { OrHomePlansCarouselClient } from './or-home-plans-carousel.client';

export const OrHomePlansCarousel = ({
  heading,
  planMetadata,
  likePlanAriaLabel,
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
  theme = EThemes.LIGHT,
  fullPlans,
}: IOrHomePlansCarouselProps): JSX.Element => {
  const { base, headingSection } = orHomePlansCarousel({
    topSpacing,
    bottomSpacing,
  });

  if (!planMetadata?.length)
    return (
      <div className="container">
        <AtText className="text-skin-error">
          No home plans were provided for HomePlansCarousel
        </AtText>
      </div>
    );

  if (planMetadata.length > 12)
    return (
      <div className="container">
        <AtText className="text-skin-error">
          A maximum of 12 plans can be used for HomePlansCarousel
        </AtText>
      </div>
    );

  return (
    <div data-theme={theme}>
      <section className="overflow-visible !pl-5 pr-5 md:items-center md:pr-0 lg:mr-0 lg:!pl-8 lg:pr-0 xl:!pl-15 2xl:!pl-20 3xl:3xl:mx-auto 3xl:!max-w-[110rem]">
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
          />
        )}
        <div data-testid="or-home-plans-carousel" className={base()}>
          {heading && (
            <div
              className={headingSection()}
              data-testid="or-home-plans-carousel-heading"
            >
              {heading && <MlHeader {...heading} />}
            </div>
          )}

          {/* SSR-only hidden cards for SEO */}
          <div id="or-homeplans-ssr" className="sr-only" aria-hidden="true">
            {(fullPlans.Plans ?? []).map(({ Plan }, idx) => (
              <MlCard
                key={idx}
                className="relative mb-16 cursor-pointer"
                variant={EMlCardsTypes.HOME_PLAN}
                data-testid="or-home-plans-carousel-card"
                imageProps={getImage(Plan)}
                href={getHomePlanUrl(Plan.Name)}
                showFavouriteButton={false}
                theme={theme as EThemes}
                homePlanProps={{
                  id: Plan.Id,
                  name: Plan.Elevation?.Name || '',
                  description: Plan.Elevation?.ExteriorStyle.Name || '',
                  features: getFeatures(Plan),
                  likePlanAriaLabel,
                  tags: [
                    {
                      children: '+ Model Tour',
                      variant: ETagVariants.FEATURED,
                    },
                    {
                      children: `${Plan.exteriorCount === 1 ? '1 Exterior' : `${Plan.exteriorCount} Exteriors`}`,
                      variant: ETagVariants.DEFAULT,
                    },
                  ],
                }}
              />
            ))}
          </div>

          {/* Client-Side Interactive Carousel */}
          <Suspense
            fallback={<div className="h-[626px]">Loading carousel...</div>}
          >
            <OrHomePlansCarouselClient
              planMetadata={planMetadata}
              likePlanAriaLabel={likePlanAriaLabel}
              theme={theme}
            />
          </Suspense>
        </div>
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
          />
        )}
      </section>
    </div>
  );
};
